import { API, graphqlOperation } from 'aws-amplify'

/**
 * Utility functions for synchronizing training results across multiple occupancies
 * When a user has multiple occupancies in the same building with the same tenant,
 * their training progress should be synchronized with the highest completion status
 */

const updateTrainingResultMutation = `
    mutation UpdateTrainingResult(
        $expectedVersion: Int!
        $input: updateTrainingResultInput
        $pk: ID!
        $sk: String!
    ) {
    updateTrainingResult(
        expectedVersion: $expectedVersion
        input: $input
        pk: $pk
        sk: $sk
    ) {
        certificateType
        completedAt
        createdAt
        d1
        d2
        expiredAt
        firstName
        lastName
        pk
        quizzesCompleted
        sk
        title {
            language
            text
        }
        updatedAt
        userType
        version
        videosCompleted
    }
}`

const createTrainingResultMutation = `
    mutation UpdateTrainingResult(
        $expectedVersion: Int!
        $input: updateTrainingResultInput
        $pk: ID!
        $sk: String!
    ) {
    updateTrainingResult(
        expectedVersion: $expectedVersion
        input: $input
        pk: $pk
        sk: $sk
    ) {
        certificateType
        completedAt
        createdAt
        d1
        d2
        expiredAt
        firstName
        lastName
        pk
        quizzesCompleted
        sk
        title {
            language
            text
        }
        updatedAt
        userType
        version
        videosCompleted
    }
}`

const listTrainingResultsQuery = `
    query ListTrainingResults(
        $sk: String!,
        $d1: String,
        $pk: String,
        $limit: Int,
        $nextToken: String
    ) {
        listTrainingResults(
            sk: $sk,
            d1: $d1,
            pk: $pk,
            limit: $limit,
            nextToken: $nextToken
        ) {
            nextToken
            items {
                certificateType
                completedAt
                createdAt
                d1
                d2
                expiredAt
                firstName
                lastName
                pk
                quizzesCompleted
                sk
                title {
                    language
                    text
                }
                updatedAt
                userType
                version
                videosCompleted
            }
        }
    }
`

/**
 * Parse d1 hierarchy to extract building and tenant IDs
 * Format: client#property#building#tenant#floor#suite
 */
export function parseD1Hierarchy(d1) {
    if (!d1) return null
    const parts = d1.split('#')
    return {
        clientId: parts[0] || null,
        propertyId: parts[2] || null,
        buildingId: parts[3] || null,
        tenantId: parts[4] || null,
        floor: parts[5] || null,
        suite: parts[6] || null,
        full: d1
    }
}

/**
 * Check if two occupancies belong to the same building
 * (tenant can be different - sync works across tenants in same building)
 */
export function isSameBuilding(d1a, d1b) {
    const hierarchyA = parseD1Hierarchy(d1a)
    const hierarchyB = parseD1Hierarchy(d1b)

    if (!hierarchyA || !hierarchyB) return false

    return hierarchyA.buildingId === hierarchyB.buildingId &&
        hierarchyA.buildingId !== null
}

/**
 * @deprecated Use isSameBuilding instead - kept for backwards compatibility
 * Check if two occupancies belong to the same building and tenant
 */
export function isSameBuildingAndTenant(d1a, d1b) {
    return isSameBuilding(d1a, d1b)
}

/**
 * Extract playlist ID from training result SK
 * SK format: TRAINING_RESULT#playlistId#status
 */
export function getPlaylistIdFromSk(sk) {
    if (!sk) return null
    const parts = sk.split('#')
    return parts[1] || null
}

/**
 * Extract status from training result SK
 */
export function getStatusFromSk(sk) {
    if (!sk) return null
    const parts = sk.split('#')
    return parts[2] || null
}

/**
 * Get training status priority (higher number = more complete)
 */
export function getStatusPriority(status) {
    const priorities = {
        'COMPLETE': 3,
        'IN_PROGRESS': 2,
        'EXPIRED': 1,
        'PENDING': 0
    }
    return priorities[status] || 0
}

/**
 * Determine the highest priority training result from a list
 */
export function getHighestPriorityResult(results) {
    if (!results || results.length === 0) return null

    return results.reduce((highest, current) => {
        const currentStatus = getStatusFromSk(current.sk)
        const highestStatus = getStatusFromSk(highest.sk)

        const currentPriority = getStatusPriority(currentStatus)
        const highestPriority = getStatusPriority(highestStatus)

        if (currentPriority > highestPriority) {
            return current
        } else if (currentPriority === highestPriority) {
            // If same priority, use the most recently updated
            const currentTime = current.updatedAt || current.completedAt || current.createdAt || 0
            const highestTime = highest.updatedAt || highest.completedAt || highest.createdAt || 0
            return currentTime > highestTime ? current : highest
        }
        return highest
    }, results[0])
}

/**
 * Fetch all training results for a user's occupancies in the same building with the same playlist
 * (works across different tenants in the same building)
 */
export async function fetchRelatedTrainingResults(userPk, playlistId, currentD1) {
    const hierarchy = parseD1Hierarchy(currentD1)
    if (!hierarchy || !hierarchy.buildingId) {
        return []
    }

    const allResults = []
    const statuses = ['COMPLETE', 'IN_PROGRESS', 'EXPIRED']

    const d1Parts = currentD1.split('#')
    const d1Prefix = `${d1Parts[0]}#${d1Parts[1]}#${d1Parts[2]}#${d1Parts[3]}`

    for (const status of statuses) {
        const sk = `TRAINING_RESULT#${playlistId}#${status}`

        try {
            const response = await API.graphql(graphqlOperation(listTrainingResultsQuery, {
                sk: sk,
                d1: d1Prefix
            }))

            const items = response.data.listTrainingResults.items || []
            const relatedResults = items.filter(result =>
                result.pk === userPk &&
                isSameBuilding(result.d1, currentD1)
            )

            allResults.push(...relatedResults)
        } catch (error) {
            console.error('SYNC ERROR fetching', status, ':', error)
        }
    }

    return allResults
}

/**
 * Sync training result across all occupancies in the same building with the same playlist
 * This ensures all occupancies have the same highest completion status
 * Works across different tenants as long as they're in the same building and have the same playlist
 */
export async function syncTrainingResultAcrossOccupancies(sourceResult, occupancies) {
    if (!sourceResult || !occupancies || occupancies.length === 0) {
        return []
    }

    const playlistId = getPlaylistIdFromSk(sourceResult.sk)
    if (!playlistId) return []

    const relatedOccupancies = occupancies.filter(occ =>
        isSameBuilding(occ.d1, sourceResult.d1)
    )

    if (relatedOccupancies.length <= 1) {
        return []
    }

    const existingResults = await fetchRelatedTrainingResults(
        sourceResult.pk,
        playlistId,
        sourceResult.d1
    )

    const highestResult = getHighestPriorityResult([...existingResults, sourceResult])
    if (!highestResult) return []

    const highestStatus = getStatusFromSk(highestResult.sk)
    const targetSk = `TRAINING_RESULT#${playlistId}#${highestStatus}`

    const existingByD2 = {}
    existingResults.forEach(result => {
        existingByD2[result.d2] = result
    })

    const syncPromises = []

    for (const occupancy of relatedOccupancies) {
        if (occupancy.d2 === sourceResult.d2) {
            continue
        }

        const existingResult = existingByD2[occupancy.d2]

        if (existingResult) {
            const existingStatus = getStatusFromSk(existingResult.sk)
            const existingPriority = getStatusPriority(existingStatus)
            const highestPriority = getStatusPriority(highestStatus)

            if (existingPriority < highestPriority) {
                const updateData = {
                    pk: existingResult.pk,
                    sk: existingResult.sk,
                    expectedVersion: existingResult.version,
                    input: {
                        sk: targetSk,
                        completedAt: highestResult.completedAt || null,
                        updatedAt: Date.now(),
                        quizzesCompleted: highestResult.quizzesCompleted || [],
                        videosCompleted: highestResult.videosCompleted || [],
                        expiredAt: highestResult.expiredAt || null,
                        d1: occupancy.d1,
                        d2: occupancy.d2
                    }
                }

                syncPromises.push(
                    API.graphql(graphqlOperation(updateTrainingResultMutation, updateData))
                        .then(result => result)
                        .catch(error => {
                            console.error('SYNC ERROR: Failed to update:', error)
                            return null
                        })
                )
            }
        } else {
            const dummySk = `TRAINING_RESULT#${playlistId}#PENDING`

            const createData = {
                pk: highestResult.pk,
                sk: dummySk,
                expectedVersion: 1,
                input: {
                    sk: targetSk,
                    completedAt: highestResult.completedAt || null,
                    createdAt: Date.now(),
                    updatedAt: Date.now(),
                    d1: occupancy.d1,
                    d2: occupancy.d2,
                    certificateType: highestResult.certificateType,
                    firstName: highestResult.firstName,
                    lastName: highestResult.lastName,
                    quizzesCompleted: highestResult.quizzesCompleted || [],
                    title: highestResult.title,
                    userType: highestResult.userType,
                    videosCompleted: highestResult.videosCompleted || [],
                    expiredAt: highestResult.expiredAt || null
                }
            }

            syncPromises.push(
                API.graphql(graphqlOperation(createTrainingResultMutation, createData))
                    .then(result => result)
                    .catch(error => {
                        console.error('SYNC ERROR: Failed to create:', error)
                        return null
                    })
            )
        }
    }

    const results = await Promise.all(syncPromises)
    const successfulResults = results.filter(r => r !== null)

    return successfulResults
}

/**
 * Get count of occupancies in the same building for display purposes
 */
export function getOccupancyCountInBuilding(occupancies, buildingId) {
    if (!occupancies || !buildingId) return 0

    return occupancies.filter(occ => {
        const hierarchy = parseD1Hierarchy(occ.d1)
        return hierarchy && hierarchy.buildingId === buildingId
    }).length
}

/**
 * Group occupancies by building only (not by tenant)
 */
export function groupOccupanciesByBuilding(occupancies) {
    if (!occupancies) return {}

    const groups = {}

    occupancies.forEach(occ => {
        const hierarchy = parseD1Hierarchy(occ.d1)
        if (!hierarchy || !hierarchy.buildingId) return

        const key = hierarchy.buildingId
        if (!groups[key]) {
            groups[key] = {
                buildingId: hierarchy.buildingId,
                occupancies: []
            }
        }
        groups[key].occupancies.push(occ)
    })

    return groups
}

/**
 * Group occupancies by building and tenant
 */
export function groupOccupanciesByBuildingAndTenant(occupancies) {
    if (!occupancies) return {}

    const groups = {}

    occupancies.forEach(occ => {
        const hierarchy = parseD1Hierarchy(occ.d1)
        if (!hierarchy || !hierarchy.buildingId || !hierarchy.tenantId) return

        const key = `${hierarchy.buildingId}#${hierarchy.tenantId}`
        if (!groups[key]) {
            groups[key] = {
                buildingId: hierarchy.buildingId,
                tenantId: hierarchy.tenantId,
                occupancies: []
            }
        }
        groups[key].occupancies.push(occ)
    })

    return groups
}

/**
 * Check if user has multiple occupancies in the same building
 */
export function hasMultipleOccupanciesInSameBuilding(occupancies) {
    const groups = groupOccupanciesByBuilding(occupancies)
    return Object.values(groups).some(group => group.occupancies.length > 1)
}

/**
 * @deprecated Use hasMultipleOccupanciesInSameBuilding instead
 * Check if user has multiple occupancies in the same building and tenant
 */
export function hasMultipleOccupanciesInSameBuildingTenant(occupancies) {
    return hasMultipleOccupanciesInSameBuilding(occupancies)
}

export default {
    parseD1Hierarchy,
    isSameBuilding,
    isSameBuildingAndTenant,
    getPlaylistIdFromSk,
    getStatusFromSk,
    getStatusPriority,
    getHighestPriorityResult,
    fetchRelatedTrainingResults,
    syncTrainingResultAcrossOccupancies,
    getOccupancyCountInBuilding,
    groupOccupanciesByBuilding,
    groupOccupanciesByBuildingAndTenant,
    hasMultipleOccupanciesInSameBuilding,
    hasMultipleOccupanciesInSameBuildingTenant
}

